/**
 * AppHeader — persistent top header.
 *
 * Layout (≥ 768px): brand left · nav center · UserMenu right.
 * Layout (< 768px): brand left · hamburger right; nav collapses into a
 * slide-down drawer toggled by the hamburger.
 *
 * Auth is enforced by RequireAuth upstream; this component assumes a
 * tenant_user is in the auth store.
 */

import { useEffect } from 'react';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';
import { t } from '@/lib/i18n';
import { Wordmark } from '@/components/Wordmark/Wordmark';
import { PRIMARY_NAV } from '../nav-config';
import { HeaderNavItem } from './HeaderNavItem';
import { UserMenu } from './UserMenu';
import styles from './AppHeader.module.css';

export function AppHeader() {
  const tenantUser = useAuthStore((s) => s.tenantUser);
  const permissions = useAuthStore((s) => s.permissions);
  // The store key is still `sidebarOpen` from the prior layout era; it now
  // means "mobile nav drawer open". Rename deferred to avoid touching every
  // selector in one pass.
  const mobileMenuOpen = useUiStore((s) => s.sidebarOpen);
  const setMobileMenuOpen = useUiStore((s) => s.setSidebarOpen);
  const toggleMobileMenu = useUiStore((s) => s.toggleSidebar);

  const visibleNav = PRIMARY_NAV.filter(
    (item) => !item.requiresPermission || permissions.has(item.requiresPermission),
  );

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileMenuOpen, setMobileMenuOpen]);

  return (
    <>
      <header className={styles.header}>
        <div className={styles.brand}>
          <Wordmark size="sm" />
          <span className={styles.brandDivider} aria-hidden="true" />
          <div className={styles.workspace} title={tenantUser?.tenantName ?? ''}>
            <span className={styles.eyebrow}>{t('Workspace')}</span>
            <span className={styles.brandName}>{tenantUser?.tenantName ?? t('—')}</span>
          </div>
        </div>

        <nav className={styles.nav} aria-label={t('Primary navigation')}>
          {visibleNav.map((item) => (
            <HeaderNavItem key={item.to} to={item.to} label={t(item.labelKey)} end={item.end} />
          ))}
        </nav>

        <div className={styles.right}>
          <UserMenu />
          <button
            type="button"
            className={styles.hamburger}
            onClick={toggleMobileMenu}
            aria-label={mobileMenuOpen ? t('Close menu') : t('Open menu')}
            aria-expanded={mobileMenuOpen}
            aria-controls="app-mobile-nav"
            data-testid="header-hamburger"
          >
            <span aria-hidden="true">{mobileMenuOpen ? '✕' : '☰'}</span>
          </button>
        </div>
      </header>

      <div
        id="app-mobile-nav"
        className={styles.mobileNav}
        data-open={mobileMenuOpen ? 'true' : 'false'}
        aria-hidden={mobileMenuOpen ? undefined : true}
      >
        {visibleNav.map((item) => (
          <HeaderNavItem
            key={item.to}
            to={item.to}
            label={t(item.labelKey)}
            end={item.end}
            variant="mobile"
          />
        ))}
      </div>
    </>
  );
}
